import React from 'react';
import { CalendarDate, today, DateFormatter, DateValue } from '@internationalized/date';
import { Button } from 'react-aria-components';
import { SearchFacet } from '../../../types/search';
import { useDateFilter } from '../contexts/PortalDateFilterProvider';

interface PortalMonthlyDateSelectorProps {
  readonly currentDateFacet: SearchFacet;
}

function getMonthRange(year: number, month: number) {
  const start = new CalendarDate(year, month, 1);
  const daysInMonth = start.calendar.getDaysInMonth(start);
  const end = new CalendarDate(year, month, daysInMonth);
  return { start, end };
}

function formatValue(start: CalendarDate, end: CalendarDate) {
  return `${start.toString()},${end.toString()}`;
}

function formatDate(date: DateValue) {
  const formatter = new DateFormatter('en-GB', {
    year: 'numeric',
    month: 'long',
  });
  return formatter.format(date.toDate('UTC'));
}

export default function PortalMonthlyDateSelector({ currentDateFacet }: PortalMonthlyDateSelectorProps) {
  const { selectedValue, setSelectedValue, setSelectedValueLabel } = useDateFilter(currentDateFacet.key);
  const [date, setDate] = React.useState(today('UTC'));
  const [datePlusOneYr, setDatePlusOneYr] = React.useState(today('UTC').add({ years: 1 }));
  const months = Array.from({ length: 12 }, (_, i) => i + 1); // 1-12
  const yearIncrement = 2;

  React.useEffect(() => {
    if (selectedValue) return;
    const { start, end } = getMonthRange(date.year, date.month);
    const value = formatValue(start, end);
    setSelectedValue(value);
    setSelectedValueLabel(`${start.toDate('UTC').toLocaleString('en-GB', { month: 'long' })} ${date.year}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function backBothYears() {
    setDate(prev => prev.subtract({ years: yearIncrement }));
    setDatePlusOneYr(prev => prev.subtract({ years: yearIncrement }));
  }

  function forwardBothYears() {
    setDate(prev => prev.add({ years: yearIncrement }));
    setDatePlusOneYr(prev => prev.add({ years: yearIncrement }));
  }

  function handleMonthSelect(month: number, year: number) {
    const { start, end } = getMonthRange(year, month);
    const value = formatValue(start, end);
    const label = formatDate(start);

    setSelectedValue(value);
    setSelectedValueLabel(label);
  }

  function buildMonthButtons(year: number) {
    return months.map(month => {
      const { start, end } = getMonthRange(year, month);
      const value = formatValue(start, end);
      const isSelected = selectedValue === value;
      const label = start.toDate('UTC').toLocaleString('en-GB', { month: 'long' });
      return (
        <Button
          key={month}
          className={`portal-date-type-selector__month-btn ${isSelected ? 'portal-date-type-selector__month-btn--selected' : ''}`}
          value={value}
          autoFocus={isSelected}
          aria-selected={isSelected}
          aria-label={`Select ${label} ${year}`}
          onPress={() => handleMonthSelect(month, year)}
        >
          {label}
        </Button>
      );
    });
  }

  return (
    <div className="portal-date-type-selector__monthly">
      <div className="portal-date-type-selector__section">
        <div className="portal-date-type-selector__nav">
          <Button onPress={backBothYears}>
            <svg className="portal-date-type-selector__back-icon icon" aria-hidden="true">
              <use href="/dist/assets/icons/sprite.svg#arrow-left"></use>
            </svg>
          </Button>
          <span>{date.year}</span>
        </div>
        <div className="portal-date-type-selector__months-list">{buildMonthButtons(date.year)}</div>
      </div>
      <div className="portal-date-type-selector__section">
        <div className="portal-date-type-selector__nav">
          <span>{datePlusOneYr.year}</span>
          <Button onPress={forwardBothYears}>
            <svg className="portal-date-type-selector__back-icon icon" aria-hidden="true">
              <use href="/dist/assets/icons/sprite.svg#arrow-right"></use>
            </svg>
          </Button>
        </div>
        <div className="portal-date-type-selector__months-list">{buildMonthButtons(datePlusOneYr.year)}</div>
      </div>
    </div>
  );
}
